import { Component } from '@angular/core';
import { ReservationsService } from '../../services/reservations.service';
import { Reservation } from '../../interfaces/reservation';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputComponent } from '../input/input.component';
import { PaymentsService } from '../../services/payments.service';
import { MatDialog } from '@angular/material/dialog';
import { PaymentResponseModalComponent } from '../payment-response-modal/payment-response-modal.component';
import { CommonService } from '../../services/common.service';
import { ApplyDiscountCodeResponseModalComponent } from '../apply-discount-code-response-modal/apply-discount-code-response-modal.component';
import { ButtonComponent } from '../button/button.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TrackingService } from '../../services/tracking.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FontAwesomeModule, InputComponent, ButtonComponent, TranslateModule],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PaymentComponent {
  reservation: Reservation | null = null;
  discountCode: string = '';
  affiliateName: string = '';

  paymentForm = new FormGroup({
    cardNumber: new FormControl('', [
      Validators.required,
      Validators.minLength(13),
      Validators.maxLength(19),
      Validators.pattern('^[0-9]+$'),
    ]),
    expMonth: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(2),
      Validators.pattern('^(0?[1-9]|1[0-2])$'),
    ]),
    expYear: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(4),
      Validators.pattern('^([0-9]{2}|20[0-9]{2})$'),
    ]),
    cvv: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(4),
      Validators.pattern('^[0-9]{3,4}$'),
    ]),
    cardFirstName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(20),
      Validators.pattern('^[a-zA-ZáéíóúÁÉÍÓÚñÑ]+$'),
    ]),
    cardLastName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(20),
      Validators.pattern('^[a-zA-ZáéíóúÁÉÍÓÚñÑ]+$'),
    ]),
  }, { updateOn: 'blur' });

  isLoading = false;
  isLoadingCoupon = false;
  applePayAvailable = false;
  selectedPaymentMethod: 'card' | 'apple-pay' = 'card';

  redirectHtml: SafeHtml | null = null;
  timer: number = 0;
  isIframeLoading: boolean = false;
  private iframeLoadTimeout: any;

  constructor(
    private reservationService: ReservationsService,
    private paymentsService: PaymentsService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private commonService: CommonService,
    private sanitizer: DomSanitizer,
    private trackingService: TrackingService
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      const locator = params['locator'];
      this.getReservation(locator);
    });

    this.route.queryParams.subscribe(params => {
      if (params['paymentFailed']) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
        this.openPaymentError('Tu pago fue rechazado. Por favor verifica los datos de tu tarjeta e intenta nuevamente.');
      }
    });

    // Detectar disponibilidad de Apple Pay
    if ((window as any).ApplePaySession) {
      this.applePayAvailable = (window as any).ApplePaySession.canMakePayments();
      if (this.applePayAvailable) {
        this.selectedPaymentMethod = 'apple-pay';
      }
    }
  }

  get formErrors(): string[] {
    const fieldLabels: Record<string, string> = {
      cardNumber: 'Número de tarjeta',
      cardFirstName: 'Nombre',
      cardLastName: 'Apellido',
      expMonth: 'Mes de expiración',
      expYear: 'Año de expiración',
      cvv: 'CVC',
    };
    const errorMessages: Record<string, string> = {
      required: 'es requerido',
      minlength: 'es muy corto',
      maxlength: 'es muy largo',
      pattern: 'tiene un formato inválido',
      invalidCardNumber: 'es inválido',
    };
    for (const [field, label] of Object.entries(fieldLabels)) {
      const control = this.paymentForm.get(field);
      if (control?.invalid && control.dirty && control.touched) {
        const firstErrorKey = Object.keys(control.errors || {})[0];
        const msg = errorMessages[firstErrorKey] ?? 'es inválido';
        return [`${label} ${msg}`];
      }
    }
    return [];
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    return /[0-9]/.test(event.key);
  }

  onlyLetters(event: KeyboardEvent): boolean {
    return /^[a-zA-ZáéíóúÁÉÍÓÚñÑ]$/.test(event.key);
  }

  isValidCC(): boolean {
    const cardNumber = this.paymentForm.get('cardNumber')?.value;
    if (!cardNumber) return false;
    const digits = cardNumber.replace(/\D/g, '');
    return digits.length >= 13 && digits.length <= 19;
  }

  getReservation(locator: string) {
    this.reservationService.getReservation(locator).subscribe(
      res => {
        if (!res) this.router.navigate(['home']);
        this.reservation = res;
        if (res.discountCode) {
          this.discountCode = res.discountCode.code;
          if (res.discountCode.owner?.name) {
            this.affiliateName = res.discountCode.owner.name;
          }
        }
      },
      error => {
        console.error('Error loading reservation:', error);
        if (error.status === 409) {
          this.openPaymentError('Esta reserva ya se encuentra paga.');
          setTimeout(() => {
            this.router.navigate(['/home']);
          }, 3000);
        } else {
          this.router.navigate(['home']);
        }
      }
    );
  }

  onPayNow() {
    if (this.isLoading) return;

    if (!this.isValidCC()) {
      this.paymentForm.get('cardNumber')?.setErrors({ invalidCardNumber: true });
    }

    this.paymentForm.markAllAsTouched();
    this.commonService.markFormGroupDirty(this.paymentForm);

    if (!this.paymentForm.valid) return;
    if (!this.reservation) return;

    this.isLoading = true;

    const formValue = this.paymentForm.value;
    const expMonth = String(formValue.expMonth).padStart(2, '0');
    const expYear = String(formValue.expYear).length === 4
      ? String(formValue.expYear).slice(-2)
      : String(formValue.expYear);

    const paymentData = {
      cardInformation: {
        cardNumber: formValue.cardNumber,
        expMonth,
        expYear,
        cvv: formValue.cvv,
        firstName: formValue.cardFirstName,
        lastName: formValue.cardLastName
      }
    };

    this.paymentsService.pay(this.reservation.locator, paymentData).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.status == 'success') {
          this.isIframeLoading = true;
          this.redirectHtml = this.sanitizer.bypassSecurityTrustHtml(res.RedirectData);
          this.timer = 180;
          this.startTimer();
          this.startIframeLoadTimeout();

          if (this.reservation && this.reservation.items) {
            const contentIds = this.reservation.items
              .filter(item => item.typeId === 1 && item.event)
              .map(item => item.event!.id);
            this.trackingService.track('Purchase', {
              value: this.reservation.total,
              currency: 'USD',
              content_ids: contentIds,
              content_type: 'product'
            });
          }
        } else {
          this.openPaymentError('Error al pagar la reserva.');
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.openPaymentError(error.error.message ?? 'Error al pagar la reserva.');
        console.error('Error paying reservation:', error);
      }
    });
  }

  startTimer() {
    const interval = setInterval(() => {
      this.timer--;
      if (this.timer <= 0) {
        clearInterval(interval);
        this.closeModal();
      }
    }, 1000);
  }

  closeModal() {
    this.redirectHtml = null;
    this.timer = 0;
    this.isIframeLoading = false;
    if (this.iframeLoadTimeout) clearTimeout(this.iframeLoadTimeout);
  }

  iframeLoaded() {
    this.isIframeLoading = false;
    if (this.iframeLoadTimeout) clearTimeout(this.iframeLoadTimeout);
  }

  startIframeLoadTimeout() {
    if (this.iframeLoadTimeout) clearTimeout(this.iframeLoadTimeout);
    this.iframeLoadTimeout = setTimeout(() => {
      this.isIframeLoading = false;
    }, 5000);
  }

  openPaymentSuccess(paymentRes: any) {
    const dialogRef = this.dialog.open(PaymentResponseModalComponent, { data: paymentRes });
    dialogRef.afterClosed().subscribe(() => this.router.navigate(['/reservations']));
    setTimeout(() => {
      dialogRef.close();
      this.router.navigate(['/reservations']);
    }, 10000);
  }

  openPaymentError(errorMessage: string) {
    this.dialog.open(PaymentResponseModalComponent, {
      data: { errorMessage, status: 'failed' }
    });
  }

  onApplePayClick() {
    if (!this.reservation) return;

    const total = this.reservation.total;
    const locator = this.reservation.locator;

    const paymentRequest: any = {
      countryCode: 'PA',
      currencyCode: 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label: 'DrinkHopping',
        amount: String(total),
      },
    };

    const session = new (window as any).ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = (event: any) => {
      this.paymentsService.validateApplePayMerchant(event.validationURL).subscribe({
        next: (merchantSession) => session.completeMerchantValidation(merchantSession),
        error: () => {
          session.abort();
          this.openPaymentError('No se pudo validar el comerciante con Apple Pay.');
        }
      });
    };

    session.onpaymentauthorized = (event: any) => {
      const applePayToken = event.payment;
      this.paymentsService.payWithApplePay(locator, applePayToken).subscribe({
        next: (res: any) => {
          if (res.approved) {
            session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);
            this.router.navigate(['/payment-success', locator]);
          } else {
            session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
            this.openPaymentError('El pago con Apple Pay fue rechazado.');
          }
        },
        error: () => {
          session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
          this.openPaymentError('Error procesando el pago con Apple Pay.');
        }
      });
    };

    session.begin();
  }

  goToReservations() {
    this.router.navigate(['reservations']);
  }

  applyDiscountCode() {
    if (!this.reservation) return;
    this.isLoadingCoupon = true;
    this.affiliateName = '';
    this.reservationService.applyDiscount(this.reservation.locator, this.discountCode).subscribe({
      next: (res) => {
        this.isLoadingCoupon = false;
        this.reservation = res.reservation;
        if (res.discountApplied && res.affiliateName) {
          this.affiliateName = res.affiliateName;
        }
        if (!res.discountApplied) {
          this.openDiscountError(res.message);
        }
      },
      error: (error) => {
        this.isLoadingCoupon = false;
        this.openDiscountError(error.error?.message ?? 'Error al aplicar codigo.');
        console.error('Error applying discount:', error);
      }
    });
  }

  openDiscountSuccess(discountRes: any) {
    this.dialog.open(ApplyDiscountCodeResponseModalComponent, {
      data: { discountRes, status: 'success' }
    });
  }

  openDiscountError(errorMessage: string) {
    this.dialog.open(ApplyDiscountCodeResponseModalComponent, {
      data: { errorMessage, status: 'failed' }
    });
  }
}
